import React, { useRef, useEffect } from 'react';
import { debugConsole } from './utils/debug-console';
import { EventBoundary } from './event-boundary';
import { jsx as ___EmotionJSX } from "@emotion/core";
var eventStyle = 'color:darkgreen;font-weight:bold';
var logPrefix = 'fe-co-analytics: provider:';
var buildMappersFromFunction = eventMapper => {
  var mappers = [];
  var onEvent = (key, mappingFunction) => {
    mappers.push({
      key,
      mappingFunction
    });
  };
  var onEventMatch = (key, mappingFunction) => {
    mappers.push({
      key: typeof key === 'string' ? new RegExp(key) : key,
      mappingFunction
    });
  };
  eventMapper({
    onEvent,
    onEventMatch
  });
  return mappers;
};
var regexRegex = /^\/([^/]*)\/$/;
var keyFromString = key => {
  var matches = regexRegex.exec(key);
  return matches ? new RegExp(matches[1]) : key;
};
var buildMappersFromObject = eventMapper => Object.keys(eventMapper).map(key => ({
  key: keyFromString(key),
  mappingFunction: eventMapper[key]
}));
export var AnalyticsProvider = _ref => {
  var _ref$eventMapper = _ref.eventMapper,
    eventMapper = _ref$eventMapper === void 0 ? {} : _ref$eventMapper,
    transport = _ref.transport,
    _ref$debug = _ref.debug,
    debug = _ref$debug === void 0 ? false : _ref$debug,
    customLogger = _ref.logger,
    children = _ref.children;
  var queued = useRef([]);
  var logger = customLogger || debugConsole(debug);
  var flushQueue = sendFunction => {
    queued.current.forEach(args => {
      try {
        sendFunction(...args);
      } catch (error) {
        logger.error('Error sending queued event', args);
        logger.error(error);
      }
    });
    queued.current = [];
  };
  var queueAnalytics = function queueAnalytics() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    if (transport) {
      transport(...args);
    } else {
      logger.info("".concat(logPrefix, " queuing event:"), ...args);
      queued.current.push(args);
    }
  };
  useEffect(() => {
    logger.info("".concat(logPrefix, " useEffect, event queue contains ").concat(queued.current.length, " items"));
    if (transport && queued.current.length > 0) {
      logger.info("".concat(logPrefix, " flushing event queue"));
      flushQueue(transport);
    }
  }, [transport]);
  var mappers = typeof eventMapper === 'object' ? buildMappersFromObject(eventMapper) : buildMappersFromFunction(eventMapper);
  var sendMappedEvent = (event, payload) => {
    logger.info("%c".concat(logPrefix, " received %c").concat(event, "%c").concat(payload ? ' with payload' : ''), '', eventStyle, '', payload);
    mappers.filter(_ref2 => {
      var key = _ref2.key;
      logger.debug("Testing against key ".concat(key, ", which is a ").concat(key instanceof RegExp ? 'RegEx' : 'string', " with event"), event);
      var result = key instanceof RegExp ? key.test(event) : key === event;
      logger.debug('Result:', result);
      return result;
    }).flatMap(_ref3 => {
      var mappingFunction = _ref3.mappingFunction;
      return mappingFunction(event, payload !== null && payload !== void 0 ? payload : {});
    }).forEach(mappedEvent => {
      if (mappedEvent) {
        logger.info("%c".concat(logPrefix, " mapped event %c").concat(event, "%c as "), '', eventStyle, '', mappedEvent);
        queueAnalytics(mappedEvent);
      }
    });
  };
  return ___EmotionJSX(EventBoundary, {
    sendEvent: sendMappedEvent,
    debug: debug,
    logger: logger
  }, children);
};